import light_background from "@shared/style-guide/img/logo-hub-light-bg.svg"
import dark_background from "@shared/style-guide/img/logo-hub-dark-bg.svg"

import { isHex, hexToRgb } from "./helpers"
import tinycolor from "tinycolor2"

import { getCurrentEndpoint } from "@shared/helpers"

const BASE_COLOR = "base"
const GRADIENT_COLOR = "gradient"
const TYPES_COLORS = ["primary", "secondary", "accent"]
const TYPES_LOGOS = ["white", "colored"]

class StyleGuide {
  colorsConfigAndLogos = {
    colors: {
      primary: {
        base: "#FF530D",
        variant: "#0937d6"
      },
      secondary: {
        base: "#607D8B"
      },
      login: {
        gradient:
          "linear-gradient(to bottom, rgba(255, 209, 189, 0.52), rgba(255, 121, 25, 0.73))"
      }
    },
    blueGrey: {
      base: "#21333c",
      gradient: "linear-gradient(270deg, #455a64 0%, #21333c 100%)",
      50: "#eceff1",
      100: "#cfd8dc",
      200: "#b0bec5",
      300: "#90a4ae",
      400: "#78909c",
      500: "#607d8b",
      600: "#546e7a",
      700: "#455a64",
      800: "#37474f",
      900: "#263238"
    },
    grey: {
      base: "#9e9e9e",
      gradient: "linear-gradient(270deg, #fafafa 0%, #eeeeee 100%)",
      50: "#fafafa",
      100: "#f5f5f5",
      200: "#eeeeee",
      300: "#e0e0e0",
      400: "#bdbdbd",
      500: "#9e9e9e",
      600: "#757575",
      700: "#616161",
      800: "#424242",
      900: "#212121"
    },
    neutral: {
      white: "#ffffff",
      black: "#000000"
    },
    semantic: {
      base: "#8bc34a",
      informative: "#1976D2",
      notice: "#FF9800"
    },
    support: {
      info: "#1976d2",
      infoGradient: "linear-gradient(181.2deg, #1976d2 3.01%, #0d47a1 88.99%)",
      success: "#8BC34A",
      successGradient:
        "linear-gradient(178.8deg, #8bc34a 8.86%, #33691e 94.84%)",
      error: "#f44336",
      errorGradient: "linear-gradient(180deg, #f44336 0%, #b71c1c 100%)",
      warn: "#ff9800",
      warnGradient: "linear-gradient(180deg, #ff9800 0%, #e65100 100%)",
      teal: "#00ffe7",
      tealGradient: "linear-gradient(180deg, #00ffe7 0%, #00665c 100%)",
      cian: "#009fe3",
      cianGradient: "linear-gradient(180deg, #009fe3 0%, #00618a 100%)",
      purple: "#5e1ae5"
    },
    indicators: {
      finished: "#607d8b",
      pending: "#ff9800",
      awaiting: "#1976d2",
      "in-progress": "#5e1ae5",
      installation: "#6EC782"
    },
    gradientRedGreen: {
      1: "#D52D29",
      2: "#E54927",
      3: "#ED6E2D",
      4: "#F1B23F",
      5: "#F8D84A",
      6: "#E5E94D",
      7: "#DBF04E",
      8: "#C2E94A",
      9: "#ACDF4A",
      10: "#67C458"
    },
    services: {
      waiting: "#009fe3",
      approved: "#8BC34A",
      disapproved: "#f44336",
      abandoned: "#f44336",
      canceled: "#f44336",
      pending: "#ff9800"
    },
    "status-info": {
      success: "#8BC34A",
      "in-progress": "#8E5FED",
      "in-queue": "#FFCC55",
      failed: "#F77B72"
    },
    logos: {
      light_background: light_background,
      dark_background: dark_background
    }
  }

  root = `:root{`

  endpoints = ["hub"]

  favicons = [
    "logo-192x192.png",
    "logo-384x384.png",
    "logo-144x144.png",
    "logo-16x16.png",
    "white-logo-32x32.png",
    "logo-150x150.png"
  ]

  getConfigsColorsAndLogos() {
    return this.colorsConfigAndLogos
  }

  getPrefixUrl() {
    return getCurrentEndpoint()
  }

  getIconCompletePath(icon) {
    const ENDPOINT = this.getPrefixUrl()
    return `app/img/${ENDPOINT}/${ENDPOINT}-${icon}`
  }

  _getUrlFavicon() {
    return this.favicons.map(favicon => this.getIconCompletePath(favicon))
  }

  _changeAllFavicon() {
    let URLs = this._getUrlFavicon()

    URLs.forEach(url => {
      this._changeFavicon(url)
    })
  }

  _changeFavicon(url_favicon) {
    const links =
      Array.from(document.querySelectorAll("link[rel*='icon']")) ||
      document.createElement("link")

    links.forEach(link => {
      if (link.attributes.sizes !== undefined) {
        let value = link.attributes.sizes.value
        if (url_favicon.includes(value, 0)) {
          link.type = "image/x-icon"
          link.rel = "shortcut icon"
          link.href = url_favicon
          document.getElementsByTagName("head")[0].appendChild(link)
        }
      }
    })
  }

  _changeTitlePage(title) {
    document.title = title
  }

  _isValidColor(color) {
    return isHex(color) && color.length === 7
  }

  _isValidGradient(nameColor) {
    return nameColor == GRADIENT_COLOR
  }

  _updateColorsRootElement(colors, typeColor) {
    Object.entries(colors).forEach(color => {
      const colorName = color[0]
      color = color[1]

      if (this._isValidColor(color)) {
        this.root += `--${typeColor}-color-${colorName}: ${color};`

        if (colorName === BASE_COLOR) {
          this.root += `--${typeColor}-color-base-rgb: ${hexToRgb(color)};`
        }
      }

      if (this._isValidGradient(colorName)) {
        this.root += `--${typeColor}-color-gradient: ${color};`
      }
    })
  }

  _updateLogosRootElement(urlLogo, typeLogo) {
    this.root += `--background-image-${typeLogo}: url(${urlLogo});`
  }

  _injectStyle() {
    this.root += "}"
    this.resetVariablesStyleGuide()

    const styleElement = document.createElement("style")
    styleElement.id = "styleGuide"
    styleElement.textContent = this.root
    document.head.appendChild(styleElement)
  }

  _returnAllColorsBasedPrimaryAndSecondary(primaryBase, secondaryBase) {
    const COLORS_TO_CREATE = {
      primary: [
        { name: "base", change: "lighten", degree: 0 },
        { name: "50", change: "lighten", degree: 30 },
        { name: "100", change: "lighten", degree: 26 },
        { name: "200", change: "lighten", degree: 23 },
        { name: "300", change: "lighten", degree: 20 },
        { name: "400", change: "lighten", degree: 17 },
        { name: "500", change: "lighten", degree: 15 },
        { name: "600", change: "lighten", degree: 12 },
        { name: "700", change: "lighten", degree: 9 },
        { name: "800", change: "lighten", degree: 6 },
        { name: "900", change: "lighten", degree: 0 }
      ],
      secondary: [
        { name: "base", change: "lighten", degree: 0 },
        { name: "50", change: "lighten", degree: 9 },
        { name: "100", change: "lighten", degree: 8 },
        { name: "200", change: "lighten", degree: 7 },
        { name: "300", change: "lighten", degree: 6 },
        { name: "400", change: "lighten", degree: 5 },
        { name: "500", change: "darken", degree: 40 },
        { name: "600", change: "lighten", degree: 3 },
        { name: "700", change: "lighten", degree: 2 },
        { name: "800", change: "lighten", degree: 1 },
        { name: "900", change: "darken", degree: 50 }
      ]
    }

    const createdPrimary = {}
    const createdSecondary = {}

    COLORS_TO_CREATE.primary.forEach(newColor => {
      const colorFormatted = tinycolor(primaryBase)
        [`${newColor.change}`](newColor.degree)
        .toString()

      createdPrimary[`${newColor.name}`] = colorFormatted
      this.colorsConfigAndLogos.colors.primary[`${newColor.name}`] =
        colorFormatted
    })

    COLORS_TO_CREATE.secondary.forEach(newColor => {
      const colorFormatted = tinycolor(secondaryBase)
        [`${newColor.change}`](newColor.degree)
        .toString()

      createdSecondary[`${newColor.name}`] = colorFormatted
      this.colorsConfigAndLogos.colors.secondary[`${newColor.name}`] =
        colorFormatted
    })

    createdPrimary["base"] = primaryBase
    createdPrimary["variant"] = tinycolor(primaryBase).lighten(10).toString()

    createdPrimary[
      "gradient"
    ] = `linear-gradient(360deg, ${createdPrimary["800"]} 0%, ${primaryBase} 100%)`
    this.colorsConfigAndLogos.colors.login.gradient = `linear-gradient(to bottom, ${tinycolor(
      primaryBase
    ).setAlpha(0.5)}, ${tinycolor(primaryBase).setAlpha(0.7)})`

    createdSecondary["base"] = secondaryBase
    createdSecondary[
      "gradient"
    ] = `linear-gradient(360deg, ${createdSecondary["900"]} 0%, ${secondaryBase} 100%)`

    return { primary: createdPrimary, secondary: createdSecondary }
  }

  async install(endpoint = "", layoutProviderAndChain) {
    this.root = ":root{"

    return new Promise((resolve, reject) => {
      const IS_LANDING_PAGE = endpoint === "hubdevendas"
      const colors = {}
      const logos = {}

      logos.white = layoutProviderAndChain.white_logo
      logos.colored = layoutProviderAndChain.colored_logo

      const allColorsConfig = this._returnAllColorsBasedPrimaryAndSecondary(
        layoutProviderAndChain.main_color,
        layoutProviderAndChain.secondary_color
      )

      colors.primary = allColorsConfig.primary
      colors.secondary = allColorsConfig.secondary

      this._changeAllFavicon()

      TYPES_LOGOS.map(type => {
        if (logos[type]) {
          this._updateLogosRootElement(logos[type], type)
        }
      })

      TYPES_COLORS.map(type => {
        if (colors[type]) {
          this._updateColorsRootElement(colors[type], type)
        }
      })

      if (IS_LANDING_PAGE) {
        this._updateColorsRootElement({ base: "#FDCE02" }, "accent")
      }

      this._injectStyle()

      resolve()
    })
  }

  resetVariablesStyleGuide() {
    const styleGuide = document.querySelector("#styleGuide")
    if (styleGuide) {
      document.head.removeChild(styleGuide)
    }
  }
}

export default new StyleGuide()
